import Fuse from 'fuse.js';
import React, { useState, useEffect, type ReactNode, useMemo } from 'react';

import type { User } from 'src/graphql/generated';
import { useUserList } from 'src/hooks/use-user-list';

interface Context {
  searchTerm: string;
  searchResults: User[] | null;
}

export const SearchUsersContext = React.createContext<Context>({
  searchTerm: '',
  searchResults: [],
});

const options = {
  keys: ['fullName', 'email'],
  threshold: 0.4,
};

export const SearchUsersProvider = ({
  children,
  searchTerm,
}: {
  children: ReactNode;
  searchTerm: string;
}) => {
  const { data: usersData } = useUserList();

  const usersOnAccount = useMemo(
    () =>
      usersData?.usersList?.users.map((user) => ({
        ...user,
        fullName: `${user.firstName} ${user.lastName}`,
      })) || [],
    [usersData],
  );
  const [searchResults, setSearchResults] = useState<User[] | null>(
    usersOnAccount,
  );

  const searchIndex = useMemo(
    () => Fuse.createIndex(options.keys, usersOnAccount),
    [usersOnAccount],
  );

  useEffect(() => {
    if (searchTerm) {
      const fuse = new Fuse(usersOnAccount, options, searchIndex);
      const results = fuse.search(searchTerm).map((result) => result.item);
      setSearchResults(results);
      return;
    }
    setSearchResults(usersOnAccount);
  }, [searchTerm, usersOnAccount, searchIndex]);

  const value = useMemo(
    () => ({
      searchResults,
      searchTerm,
    }),
    [searchResults, searchTerm],
  );

  return (
    <SearchUsersContext.Provider value={value}>
      {children}
    </SearchUsersContext.Provider>
  );
};

export const useSearchUsers = () => {
  const context = React.useContext(SearchUsersContext);
  if (context === undefined) {
    throw new Error('useSearchUsers must be used within a SearchUsersProvider');
  }
  return context;
};
